import { Injectable, signal, computed } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';
  private readonly _themeMode = signal<ThemeMode>('system');
  private readonly _isDark = signal<boolean>(false);

  // Public readonly signals
  readonly themeMode = this._themeMode.asReadonly();
  readonly isDark = this._isDark.asReadonly();
  readonly isLight = computed(() => !this._isDark());

  constructor() {
    this.initializeTheme();
    this.setupSystemThemeListener();
  }

  private initializeTheme(): void {
    const savedTheme = localStorage.getItem(this.THEME_KEY) as ThemeMode;
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      this._themeMode.set(savedTheme);
    }
    this.applyTheme();
  }

  private setupSystemThemeListener(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        if (this._themeMode() === 'system') {
          this._isDark.set(e.matches);
          this.updateDocumentClass();
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      
      // Initial check
      if (this._themeMode() === 'system') {
        this._isDark.set(mediaQuery.matches);
        this.updateDocumentClass();
      }
    }
  }

  setTheme(mode: ThemeMode): void {
    this._themeMode.set(mode);
    localStorage.setItem(this.THEME_KEY, mode);
    this.applyTheme();
  }

  toggleTheme(): void {
    const currentMode = this._themeMode();
    if (currentMode === 'system') {
      // If system, toggle to opposite of current system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(systemPrefersDark ? 'light' : 'dark');
    } else {
      // Toggle between light and dark
      this.setTheme(currentMode === 'light' ? 'dark' : 'light');
    }
  }

  private applyTheme(): void {
    const mode = this._themeMode();
    
    switch (mode) {
      case 'light':
        this._isDark.set(false);
        break;
      case 'dark':
        this._isDark.set(true);
        break;
      case 'system':
        if (typeof window !== 'undefined' && window.matchMedia) {
          this._isDark.set(window.matchMedia('(prefers-color-scheme: dark)').matches);
        } else {
          this._isDark.set(false);
        }
        break;
    }
    
    this.updateDocumentClass();
  }

  private updateDocumentClass(): void {
    if (typeof document !== 'undefined') {
      const html = document.documentElement;
      if (this._isDark()) {
        html.classList.add('dark-theme');
        html.classList.remove('light-theme');
      } else {
        html.classList.add('light-theme');
        html.classList.remove('dark-theme');
      }
    }
  }

  // Utility methods for components
  getThemeIcon(): string {
    const mode = this._themeMode();
    if (mode === 'system') {
      return this._isDark() ? 'brightness_auto' : 'brightness_auto';
    }
    return this._isDark() ? 'light_mode' : 'dark_mode';
  }

  getThemeLabel(): string {
    const mode = this._themeMode();
    switch (mode) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
      default:
        return 'System';
    }
  }

  getNextThemeMode(): ThemeMode {
    const current = this._themeMode();
    switch (current) {
      case 'light':
        return 'dark';
      case 'dark':
        return 'system';
      case 'system':
        return 'light';
      default:
        return 'light';
    }
  }
}
